import { type FC, useCallback, useMemo } from "react";
import { Table } from "semantic-ui-react";
import { type ColumnOption } from "../../../../../interfaces/columnOptions";
import { type SurveyQuestionAnsweredUser } from "../../types/state";
import { PerformanceTabSectionList } from "features/Library/Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";

import "./surveyPerformanceDrilldownList.scss";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { RouteNames } from "enums";
import {
  isUserDeleted,
  formatUserName,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import { type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { MoboLink } from "components/MoboLink/MoboLink";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export interface Props extends PropsFromRedux {
  answers: SurveyQuestionAnsweredUser[];
  isFreeTextCompatible: boolean;
  isFreeTextQuestion: boolean;
  hasSingleAccountFilter: boolean;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export const SurveyPerformanceDrilldownList: FC<Props> = ({
  answers,
  accountId,
  isFreeTextCompatible,
  hasSingleAccountFilter,
  isFreeTextQuestion,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const columns: ColumnOption<SurveyQuestionAnsweredUser>[] = useMemo(() => {
    const columns: ColumnOption<SurveyQuestionAnsweredUser>[] = [
      {
        isSortable: false,
        name: "Name",
        width: 7,
        render(user) {
          const formattedNameDisplay = formatUserName(user.FirstName, user.LastName);
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink = !isUserDeleted(user) && (!includeAccounts || childAccountEnabled || !childAccountId);
          const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
            user.AccountId,
            accountId,
            childAccountId,
            hasCreateAccountsAddOn,
          );
          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount,
            childAccountId,
            fallbackAccountId: user.AccountId,
          });

          const userName = (
            <Tooltip content={formattedNameDisplay} target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} />
          );
          return (
            <div>
              {shouldShowLink ? (
                <MoboLink
                  className="linkButton"
                  target={linkTarget}
                  to={`/${RouteNames.peopleUsers}/${user.UserId}`}
                  accountId={linkAccountId}
                >
                  {userName}
                </MoboLink>
              ) : (
                userName
              )}
              <div className="userDepartmentTitle">
                {/* Remove empty job title / department */}
                <Tooltip
                  target={<TextTruncate>{[user.Department, user.Title].filter((v) => v).join(", ")}</TextTruncate>}
                  content={`${[user.Department, user.Title].filter((v) => v).join(", ")}`}
                />
              </div>
            </div>
          );
        },
      },
      {
        name: "Email",
        width: 7,
        isSortable: false,
        render(user) {
          return <Tooltip target={<TextTruncate>{user.Email}</TextTruncate>} content={user.Email} />;
        },
      },
      {
        name: "Date",
        width: 7,
        isSortable: false,
        render(user) {
          return formatDateTime(user.AnswerDate);
        },
      },
      {
        name: "Account",
        width: 7,
        isSortable: false,
        render(user) {
          return user.AccountName ?? "-";
        },
      },
    ];

    if (hasSingleAccountFilter) {
      return columns.filter((column) => column.name !== "Account");
    }

    return columns;
  }, [accountId, hasSingleAccountFilter, formatDateTime, includeAccounts, childAccountId, hasCreateAccountsAddOn]);

  const buildRow = useCallback(
    (item: SurveyQuestionAnsweredUser) => {
      return columns.map((column, index) => (
        <Table.Cell width={column.width} key={`${column.name}_${index}`}>
          {column.render(item)}
        </Table.Cell>
      ));
    },
    [columns],
  );

  const shouldDisplayOtherText = useCallback(
    // If IsOtherQuestion, then AnswerResult == "true" or AnswerResult being undefined
    // means other option was selected but text response not required
    (answer: SurveyQuestionAnsweredUser): boolean => !!answer.AnswerResult && isFreeTextCompatible,
    [isFreeTextCompatible],
  );

  return (
    <PerformanceTabSectionList
      title={isFreeTextQuestion ? "Responses" : "People"}
      count={answers.length}
      renderList={(page) => (
        <div className="survey-drilldown-list">
          <Table sortable className="list-table">
            <Table.Header>
              <Table.Row>
                {
                  // These columns will be fixed / never change, so index
                  // is acceptable
                  columns.map((col, i) => (
                    <Table.HeaderCell width={col.width} key={i}>
                      {col.name}
                    </Table.HeaderCell>
                  ))
                }
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {answers.slice((page - 1) * 10, page * 10).map((answer, i) => (
                <Table.Row key={`${answer.UserId} ${i}`}>
                  <Table.Cell width={16} className="response-container">
                    <Table className="layout-table">
                      <Table.Body>
                        <Table.Row>{buildRow(answer)}</Table.Row>
                        {shouldDisplayOtherText(answer) && (
                          <Table.Row>
                            <Table.Cell width={16} className="other-response">
                              <div className="answer">{`[Free Text Response] ${answer.AnswerResult}`}</div>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveyPerformanceDrilldownList);
export default ConnectedComponent;
